import React, { useEffect, useState } from 'react';
import { ArrowRight, Clock3, ShieldCheck } from 'lucide-react';
import { baseapi } from '../baseapi';

const duration = (minutes: number | null | undefined) => {
  if (minutes == null || !Number.isFinite(minutes)) return '—';
  const rounded = Math.round(minutes);
  const hours = Math.floor(rounded / 60);
  const mins = rounded % 60;
  return hours ? `${hours} hr ${mins} min` : `${mins} min`;
};

/** Compact verified journey averages for the Sectors page. */
export const DarshanJourneySummary: React.FC = () => {
  const [summary, setSummary] = useState<any>(null);

  useEffect(() => {
    let alive = true;
    const load = () => {
      baseapi.get('darshan-evidence/summary')
        .then(response => { if (alive) setSummary(response.data); })
        .catch(() => {});
    };
    load();
    const timer = window.setInterval(() => {
      if (!document.hidden) load();
    }, 30000);
    return () => { alive = false; window.clearInterval(timer); };
  }, []);

  const journeys = [
    { id: 'K' as const, title: 'Kummaripalem Q line' },
    { id: 'V' as const, title: 'Vinayaka Q line' },
  ].map(route => {
    const whole = summary?.daily?.[route.id];
    const first = summary?.daily_segments?.[`${route.id}>T`];
    const exit = summary?.daily_segments?.['T>X'];
    const hasWholeJourney = whole?.minutes != null;
    const total = hasWholeJourney ? whole.minutes : first?.avg != null && exit?.avg != null
      ? first.avg + exit.avg
      : null;
    return {
      ...route,
      total,
      measured: hasWholeJourney && whole?.basis === 'same_person',
      journeyCount: hasWholeJourney ? whole?.n : null,
      firstMinutes: first?.avg ?? null,
      firstCount: first?.confirmed ?? 0,
      exitMinutes: exit?.avg ?? null,
      exitCount: exit?.confirmed ?? 0,
    };
  });

  return (
    <section className="rounded-xl border border-neutral-200/80 bg-white p-3 shadow-xs dark:border-[#262b37] dark:bg-[#181b22] sm:p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-1.5">
        <div>
          <h2 className="text-base font-bold text-[#7c2024] dark:text-rose-200">
            <Clock3 className="mr-1.5 inline h-4 w-4 -mt-0.5 text-blue-600" />
            How long does darshan take?
          </h2>
          <p className="text-xs text-neutral-500 dark:text-neutral-400">Typical time from each queue entrance to the Antharalayam exit</p>
        </div>
        <span className="text-[11px] text-neutral-500 dark:text-neutral-400">Updates every 30 seconds</span>
      </div>

      <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
        {journeys.map(route => (
          <article key={route.id} className="rounded-xl border border-neutral-200 bg-white p-3 dark:border-[#303746] dark:bg-[#151922] sm:p-4">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <h3 className="font-bold text-neutral-900 dark:text-neutral-100">{route.title}</h3>
              <div className="text-right">
                <div className="text-[11px] font-semibold text-blue-700 dark:text-blue-300">
                  {route.measured && <ShieldCheck className="mr-1 inline h-3.5 w-3.5 text-emerald-600" />}
                  {route.measured ? 'Measured journey' : 'Estimated journey'}
                </div>
                <div className="text-2xl font-black leading-tight tabular-nums text-blue-700 dark:text-blue-300">{duration(route.total)}</div>
                <div className="text-[11px] text-neutral-500 dark:text-neutral-400">
                  {route.journeyCount != null ? `${route.journeyCount} matched people` : 'Estimated from the two legs'}
                </div>
              </div>
            </div>

            <div className="mt-3 grid grid-cols-[1fr_auto_1fr] items-center gap-2">
              <div className="rounded-lg border border-neutral-200 px-3 py-2 dark:border-[#303746]">
                <div className="text-xs font-semibold text-neutral-600 dark:text-neutral-300">Queue line → Tollgate</div>
                <div className="text-lg font-black tabular-nums">{duration(route.firstMinutes)}</div>
                <div className="text-[11px] text-neutral-500 dark:text-neutral-400">{route.firstCount ? `${route.firstCount} matched people` : 'No verified matches yet'}</div>
              </div>
              <ArrowRight className="h-4 w-4 text-neutral-400" />
              <div className="rounded-lg border border-neutral-200 px-3 py-2 dark:border-[#303746]">
                <div className="text-xs font-semibold text-neutral-600 dark:text-neutral-300">Tollgate → Exit</div>
                <div className="text-lg font-black tabular-nums">{duration(route.exitMinutes)}</div>
                <div className="text-[11px] text-neutral-500 dark:text-neutral-400">{route.exitCount ? `${route.exitCount} matched people` : 'No verified matches yet'}</div>
              </div>
            </div>
          </article>
        ))}
      </div>
      <p className="mt-2 text-[11px] text-neutral-500 dark:text-neutral-400">Times use verified camera matches. A journey is marked measured only when the same person was confirmed from queue entry to exit.</p>
    </section>
  );
};

export default DarshanJourneySummary;
